import {match} from 'ts-pattern';

import {isNil} from 'ramda';

import {getSafely} from 'shared/web';

import {KeyboardShortcut} from '../constants/KeyboardShortcut';
import {KEYBOARD_SHORTCUT_DEFINITIONS} from '../constants/keyboardShortcutDefinitions';

type ShortcutEntry = {
  invoke: () => boolean;
};

const stacks = new Map<KeyboardShortcut, ShortcutEntry[]>();

let isListenerAttached = false;

const findMatchingStack = (event: KeyboardEvent) => {
  for (const [shortcut, stack] of stacks) {
    if (stack.length === 0) {
      continue;
    }

    const definition = getSafely(KEYBOARD_SHORTCUT_DEFINITIONS, shortcut);
    if (isNil(definition) || !definition.matches(event)) {
      continue;
    }

    return stack;
  }

  return undefined;
};

const onWindowKeyDown = (event: KeyboardEvent) => {
  if (event.defaultPrevented) {
    return;
  }

  const stack = findMatchingStack(event);
  if (isNil(stack)) {
    return;
  }

  const isHandled = stack[stack.length - 1]?.invoke() ?? false;
  if (isHandled) {
    event.preventDefault();
  }
};

const attachListener = () => {
  if (isListenerAttached || typeof window === 'undefined') {
    return;
  }

  window.addEventListener('keydown', onWindowKeyDown);
  isListenerAttached = true;
};

const detachListenerIfIdle = () => {
  if (!isListenerAttached || typeof window === 'undefined') {
    return;
  }

  const hasActiveRegistration = [...stacks.values()].some((stack) => stack.length > 0);
  if (hasActiveRegistration) {
    return;
  }

  window.removeEventListener('keydown', onWindowKeyDown);
  isListenerAttached = false;
};

/**
 * Registers a shortcut handler. Later registrations win (LIFO).
 * Returns an unregister function that restores the previous handler.
 */
export function registerShortcut(shortcut: KeyboardShortcut, invoke: () => boolean): VoidFunction {
  const existing = stacks.get(shortcut) ?? [];

  if (process.env.NODE_ENV !== 'production' && existing.length > 0) {
    console.error(
      `[KeyboardShortcut] Duplicate registration for "${shortcut}". ` +
        `The most recently mounted button wins (LIFO).`
    );
  }

  const entry: ShortcutEntry = {invoke};
  const nextStack = [...existing, entry];
  stacks.set(shortcut, nextStack);
  attachListener();

  return function unregisterShortcut() {
    const current = stacks.get(shortcut);
    if (isNil(current)) {
      return;
    }

    const withoutEntry = current.filter((candidate) => candidate !== entry);
    match(withoutEntry.length)
      .with(0, () => {
        stacks.delete(shortcut);
      })
      .otherwise(() => {
        stacks.set(shortcut, withoutEntry);
      });

    detachListenerIfIdle();
  };
}

/** @internal Test helper — clears all registrations and the window listener. */
export function __resetKeyboardShortcutRegistryForTests() {
  stacks.clear();

  if (isListenerAttached && typeof window !== 'undefined') {
    window.removeEventListener('keydown', onWindowKeyDown);
  }

  isListenerAttached = false;
}
